import { getAuthHeaders, handleAuthError } from "../../../../utils/authUtils";
import { API_BASE_URL } from "../../../../environment";
import { requestExportByQuery } from "../../Dashboard/dashboardApi";

export interface SalesActivityTypeStatusCount {
  crm_sales_activity_type_status_id: number;
  crm_sales_activity_type_status_display_name: string;
  counts: number;
}

export interface SalesActivityTypeCount {
  crm_sales_activity_type_id: number;
  crm_sales_activity_type_display_name: string;
  icon?: string | null;
  counts: number;
  statuses?: SalesActivityTypeStatusCount[];
}

export interface SalesActivityTypeStatusGroup {
  crm_sales_activity_type_id: number;
  crm_sales_activity_type_display_name: string;
  statuses: SalesActivityTypeStatusCount[];
}

export interface SalesActivityEntityCounts {
  [entity: string]: number;
}

export interface SalesActivityStatusEntry {
  [status: string]: SalesActivityEntityCounts[];
}

export interface SalesActivityTypeEntityEntry {
  [type: string]: SalesActivityEntityCounts[] | {
    icon?: string;
    statuses: SalesActivityStatusEntry[];
  } | SalesActivityStatusEntry[];
}

export interface SalesActivityTypeStatusEntityEntry {
  [type: string]: SalesActivityStatusEntry[];
}

export interface SalesActivityDashboardResponse {
  crm_sales_activity_type_counts: SalesActivityTypeCount[];
  crm_sales_activity_types_by_crm_entity: SalesActivityTypeEntityEntry[];
  crm_sales_activity_type_status_counts?: SalesActivityTypeStatusGroup[];
  crm_sales_activity_type_statuses_by_crm_entity?: SalesActivityTypeStatusEntityEntry[];
}

export interface CrmEntity {
  crm_entity_id: number;
  crm_entity_key: string;
  crm_entity_display_name: string;
}

export interface SalesActivityFilters {
  startDatetime?: string | null;
  endDatetime?: string | null;
  crmEntityId?: number | null;
  crmSalesActivityTypeId?: number | null;
  crmSalesActivityTypeStatusId?: number | null;
  salesUserId?: number | null;
}

export interface FlattenedActivityEntityRow {
  type: string;
  status: string;
  entityCounts: SalesActivityEntityCounts;
}

export interface ActivityEntityTypeTotal {
  type: string;
  entityCounts: SalesActivityEntityCounts;
}

export const ALLOWED_CRM_ENTITY_NAMES = [
  "Company",
  "Contact",
  "Lead",
  "Pipeline",
  "Deal",
  "Proposal",
  "Contract",
];

const raiseForStatus = async (response: Response, fallbackMessage: string) => {
  if (response.status === 401 || response.status === 403) {
    handleAuthError({ response: { status: response.status } });
  }
  throw new Error(`${fallbackMessage} (${response.status})`);
};

const buildDashboardQuery = (
  tenantId: string | number,
  filters: SalesActivityFilters
) => {
  const params = new URLSearchParams();
  params.append("tenant_id", String(tenantId));
  if (filters.startDatetime) {
    params.append("start_datetime", filters.startDatetime);
  }
  if (filters.endDatetime) {
    params.append("end_datetime", filters.endDatetime);
  }
  if (filters.crmEntityId) {
    params.append("crm_entity_id", String(filters.crmEntityId));
  }
  if (filters.crmSalesActivityTypeId) {
    params.append(
      "crm_sales_activity_type_id",
      String(filters.crmSalesActivityTypeId)
    );
  }
  if (filters.crmSalesActivityTypeStatusId) {
    params.append(
      "crm_sales_activity_type_status_id",
      String(filters.crmSalesActivityTypeStatusId)
    );
  }
  if (filters.salesUserId) {
    params.append("sales_user_id", String(filters.salesUserId));
  }
  return params.toString();
};

export const fetchSalesActivityDashboard = async (
  tenantId: string | number,
  filters: SalesActivityFilters = {}
): Promise<SalesActivityDashboardResponse> => {
  const query = buildDashboardQuery(tenantId, filters);
  const response = await fetch(
    `${API_BASE_URL}/dashboard/sales-activity/?${query}`,
    { headers: getAuthHeaders() }
  );
  if (!response.ok) {
    await raiseForStatus(response, "Failed to load sales activity dashboard");
  }
  const data: SalesActivityDashboardResponse = await response.json();

  const statusMap = new Map<number, SalesActivityTypeStatusCount[]>();
  const statusNameMap = new Map<string, SalesActivityTypeStatusCount[]>();

  (data.crm_sales_activity_type_status_counts || []).forEach((group) => {
    if (group.crm_sales_activity_type_id) {
      statusMap.set(group.crm_sales_activity_type_id, group.statuses || []);
    }
    if (group.crm_sales_activity_type_display_name) {
      statusNameMap.set(group.crm_sales_activity_type_display_name, group.statuses || []);
    }
  });

  const mergedTypes: SalesActivityTypeCount[] = (
    data.crm_sales_activity_type_counts || []
  ).map((type) => ({
    ...type,
    statuses:
      statusMap.get(type.crm_sales_activity_type_id) ||
      statusNameMap.get(type.crm_sales_activity_type_display_name) ||
      type.statuses ||
      [],
  }));

  return {
    ...data,
    crm_sales_activity_type_counts: mergedTypes,
    crm_sales_activity_type_status_counts:
      data.crm_sales_activity_type_status_counts || [],
    crm_sales_activity_type_statuses_by_crm_entity:
      data.crm_sales_activity_type_statuses_by_crm_entity || [],
    crm_sales_activity_types_by_crm_entity:
      data.crm_sales_activity_types_by_crm_entity || [],
  };
};

export const exportSalesActivityExcel = (tenantId: string | number, filters: SalesActivityFilters = {}) =>
  requestExportByQuery("/dashboard/sales-activity/export/", buildDashboardQuery(tenantId, filters), "activities report");

export const fetchAllowedCrmEntities = async (): Promise<CrmEntity[]> => {
  const response = await fetch(`${API_BASE_URL}/crm-entity/?page_size=200`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    await raiseForStatus(response, "Failed to load CRM entities");
  }
  const data = await response.json();
  const results: CrmEntity[] = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data)
    ? data
    : [];
  return results.filter((entity) =>
    ALLOWED_CRM_ENTITY_NAMES.includes(entity.crm_entity_display_name)
  );
};

export const flattenActivityByEntity = (
  data: any[] = []
): FlattenedActivityEntityRow[] => {
  const rows: FlattenedActivityEntityRow[] = [];
  data.forEach((typeEntry) => {
    Object.entries(typeEntry || {}).forEach(([type, typeInfo]: [string, any]) => {
      const items = Array.isArray(typeInfo)
        ? typeInfo
        : (typeInfo && Array.isArray(typeInfo.statuses))
        ? typeInfo.statuses
        : [];

      items.forEach((item: any) => {
        if (!item || typeof item !== "object") return;
        const entries = Object.entries(item);
        if (entries.length === 0) return;
        const firstVal = entries[0][1];
        if (typeof firstVal === "number") {
          rows.push({
            type,
            status: "",
            entityCounts: item,
          });
        } else {
          entries.forEach(([status, entityCountsArr]: [string, any]) => {
            rows.push({
              type,
              status,
              entityCounts: Array.isArray(entityCountsArr)
                ? entityCountsArr[0] || {}
                : entityCountsArr || {},
            });
          });
        }
      });
    });
  });
  return rows;
};

// Collapses the per-status rows down to one row per activity type (summing
// every status's entity counts together) — keeps the entity heatmap's row
// count bounded to the number of *types* instead of growing with every
// type/status combination.
export const aggregateActivityByEntityType = (
  rows: FlattenedActivityEntityRow[]
): ActivityEntityTypeTotal[] => {
  const byType = new Map<string, SalesActivityEntityCounts>();
  rows.forEach((row) => {
    const totals = byType.get(row.type) || {};
    Object.entries(row.entityCounts).forEach(([entity, count]) => {
      totals[entity] = (totals[entity] || 0) + (count || 0);
    });
    byType.set(row.type, totals);
  });
  return Array.from(byType.entries()).map(([type, entityCounts]) => ({
    type,
    entityCounts,
  }));
};
